import {createContext, use, useMemo, useState, type ReactNode} from 'react'

export type DockedPanelType = 'comments' | 'alerts' | 'overview' | null

interface DockedPanelActions {
  openPanel: (panel: Exclude<DockedPanelType, null>) => void
  closePanel: () => void
  togglePanel: (panel: Exclude<DockedPanelType, null>) => void
}

// Split contexts to prevent unnecessary re-renders:
// - Components that only need to trigger actions won't re-render when panel state changes
// - Components that need to read state will subscribe to state context
export const DockedPanelStateContext = createContext<DockedPanelType>(null)

const defaultActions: DockedPanelActions = {
  openPanel: () => undefined,
  closePanel: () => undefined,
  togglePanel: () => undefined,
}

export const DockedPanelDispatchContext = createContext<DockedPanelActions>(defaultActions)

interface DockedPanelProviderProps {
  children: ReactNode
}

export function DockedPanelProvider({children}: DockedPanelProviderProps) {
  const [dockedPanel, setDockedPanel] = useState<DockedPanelType>(null)

  // Memoize actions object to keep dispatch context value stable
  // This ensures components using only useDockedPanelActions() won't re-render on state changes
  const actions = useMemo<DockedPanelActions>(
    () => ({
      openPanel: (panel: Exclude<DockedPanelType, null>) => setDockedPanel(panel),
      closePanel: () => setDockedPanel(null),
      togglePanel: (panel: Exclude<DockedPanelType, null>) =>
        setDockedPanel(current => (current === panel ? null : panel)),
    }),
    [],
  )

  return (
    <DockedPanelStateContext value={dockedPanel}>
      <DockedPanelDispatchContext value={actions}>{children}</DockedPanelDispatchContext>
    </DockedPanelStateContext>
  )
}

/** Returns the currently open docked panel name, or null if closed */
export function useDockedPanel(): DockedPanelType {
  return use(DockedPanelStateContext)
}

/** Returns memoized actions to control the docked panel - stable reference, won't cause re-renders */
export function useDockedPanelActions(): DockedPanelActions {
  return use(DockedPanelDispatchContext)
}

/** Returns whether any docked panel is currently open */
export function useIsDockedPanelOpen(): boolean {
  return use(DockedPanelStateContext) !== null
}
